import {FC, useContext, useState, useEffect, useRef} from "react";
import {Box, BoxProps, Button, LinearProgress, Stack, Switch, Tooltip} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {GRID_CHECKBOX_SELECTION_COL_DEF, GridColumns} from "@mui/x-data-grid-pro";
import {IAdminUsersCoursesResponseEntry, IPaginatedResponse, IUserCourseEnrollmentStatus, IUserEntity} from "@buildwitt/shapeshift-types";
import {Add, Check, Undo} from "@mui/icons-material";
import {AdminBulkEnrollmentModal} from "@/components/admin/bulkEnrollment/AdminBulkEnrollmentModal";
import {AdminBulkCourseCompletionModal} from "@/components/admin/bulkEnrollment/AdminBulkCourseCompletionModal";
import {AdminBulkCourseUnenrollModal} from "@/components/admin/bulkEnrollment/AdminBulkCourseUnenrollmentModal";
import {CustomToolbar} from "@/components/admin/bulkEnrollment/CustomToolbar";
import {PageBaseContext} from "@/contexts/pageBaseContext";
import {AdminTable} from "@/components/common";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import useDevCycleSdk from "@/hooks/useDevCycleSdk";
import useCustomSnackbar from "@/snackbars/useCustomSnackbar";
import {BulkEnrollmentConfirmationModal} from "./BulkEnrollment/BulkEnrollmentConfirmationModal";
import {BulkActionStatus, useManageBulkActionStatusContext} from "@/contexts/manageBulkActionStatusContext";

const useStyles = makeStyles()((theme) => ({
	contentBox: {
		display: "flex",
		flexDirection: "column",
		marginTop: theme.spacing(3),
		padding: theme.spacing(2),
		backgroundColor: theme.palette.background.paper,
		borderWidth: theme.borderWidth.small,
		borderColor: theme.palette.grey[100],
		borderStyle: "solid",
		borderRadius: theme.borderRadius.small
	},
	enrollmentAdminTable: {
		padding: 0,
		border: 0
	},
	progressBar: {
		width:"100%"
	},
	progressBarRoot:{
		width:"100%",
		alignItems:"center",
		gap: theme.spacing(2)
	},
	enrollmentDataGrid: {
		padding: 0,
		border: 0
	}
}));

interface ICustomProps {
	courseId: number;
	course?: IAdminUsersCoursesResponseEntry
}

export type IProps = Omit<BoxProps, "children"> & ICustomProps;

export const EnrollmentTab:FC<IProps> = ({courseId, course,...baseProps}) => {
	const {classes} = useStyles();
	const [bulkEnrollModalOpen, setBulkEnrollModalOpen] = useState<boolean>(false);
	const [bulkUnEnrollModalOpen, setBulkUnEnrollModalOpen] = useState<boolean>(false);
	const [bulkCompletionModalOpen, setBulkCompletionModalOpen] = useState<boolean>(false);
	const [bulkEnrollmentConfirmationModalOpen, setBulkEnrollmentConfirmationModalOpen] = useState<boolean>(false);
	const [selected, setSelected] = useState<number[]>([]);
	const [selectedSingular, setSelectedSingular] = useState<number>();
	const [{ownedCohort}] = useContext(PageBaseContext)
	const {t} = useSSTranslation();
	const {courseBulkEnrollment} = useDevCycleSdk();
	const {courseEnrollment: {courseEnrollmentStatus, setCourseEnrollmentStatus, updateCourseEnrollmentSnackbarId}} = useManageBulkActionStatusContext();
	const [enrollmentStatusAtMounting, setEnrollmentStatusAtMounting] = useState(courseEnrollmentStatus);
	const {closeSnackbar, processInProgressSnackbar} = useCustomSnackbar();
	const refreshDataGridRef = useRef<(() => void) | null>(null);
	const clearSelectionDataGridRef = useRef<(() => void) | null>(null);
	const isEnrollmentLoading = courseEnrollmentStatus === BulkActionStatus.inProgress;
	const isCoursePublished = !!course?.publishedAt;

	useEffect(() => {
		if (courseEnrollmentStatus === BulkActionStatus.complete){
			clearSelectionDataGridRef.current && clearSelectionDataGridRef.current();
			setSelected([]);
			setSelectedSingular(undefined);

			if (refreshDataGridRef.current && enrollmentStatusAtMounting !== BulkActionStatus.complete) {
				refreshDataGridRef.current();
			}

			setCourseEnrollmentStatus(BulkActionStatus.none);
			setEnrollmentStatusAtMounting(BulkActionStatus.none);
		}
	}, [courseEnrollmentStatus]);

	const handleEnrollmentLoadingChange = (isLoading: boolean) => {
		if (isLoading){
			setCourseEnrollmentStatus(BulkActionStatus.inProgress);
			const toastId = processInProgressSnackbar(
				t("bulk_enrollment_progress_msg", "Enrolling users… Don't close or refresh this window."),
				true,
				true
			);
			updateCourseEnrollmentSnackbarId(toastId);
		}
	};

	const handleEnrollmentClose = () => {
		setSelectedSingular(undefined);
		setBulkEnrollmentConfirmationModalOpen(false);
	};

	useEffect(() => {
		const handleCourseEnrollmentsCompleted = (event: CustomEvent) => {
			const {inProgress} = event.detail;
			if (!inProgress) {
				// Clear selection when enrollment fails to start
				clearSelectionDataGridRef.current && clearSelectionDataGridRef.current();
				setSelected([]);
				setSelectedSingular(undefined);
				setCourseEnrollmentStatus(BulkActionStatus.none);
				closeSnackbar(null);
				updateCourseEnrollmentSnackbarId(null);
			}
		};

		window.addEventListener("courseEnrollmentsCompleted", handleCourseEnrollmentsCompleted as EventListener);

		return () => {
			window.removeEventListener("courseEnrollmentsCompleted", handleCourseEnrollmentsCompleted as EventListener);
		};
	}, [setCourseEnrollmentStatus, closeSnackbar, updateCourseEnrollmentSnackbarId]);

	const headCells: GridColumns<IPaginatedResponse<IUserCourseEnrollmentStatus[]>["data"][0]> = ([
		{
			...GRID_CHECKBOX_SELECTION_COL_DEF,
			type: "string",
			disableColumnMenu: true
		},
		{
			field: "firstname",
			flex: 1,
			headerName: "First Name",
			type: "string"
		},
		{
			field: "lastname",
			flex: 1,
			headerName: "Last Name",
			type: "string"
		},
		{
			field: "email",
			flex: 1,
			headerName: "Email",
			type: "string"
		},
		{
			field: "jobCategory",
			flex: 1,
			headerName: "Job Category",
			type: "string"
		},
		{
			field: "jobTitle",
			flex: 1,
			headerName: "Job Title",
			type: "string"
		},
		{
			field: "hireDate",
			flex: 1,
			headerName: "Hire Date",
			type: "date",
			valueGetter: (params) => params.row.hireDate ? new Date(params.row.hireDate).toLocaleDateString("en-US", {timeZone: "UTC"}) : null
		},
		{
			field: "titleStartDate",
			flex: 1,
			headerName: "Title Start Date",
			type: "date",
			valueGetter: (params) => params.row.titleStartDate ? new Date(params.row.hireDate).toLocaleDateString("en-US", {timeZone: "UTC"}) : null
		},
		{
			field: "enrolled",
			flex: 1,
			headerName: "Enroll",
			type: "boolean",
			renderCell: e => {
				return(
					<span>
						<Tooltip title={!isCoursePublished ? t("course_draft_not_enrollment_description", "Please publish the course to enable user enrollments.") : ""}>
							<span>
								<Switch onClick={(x) => {
									x.stopPropagation();
									if(e.value){
										setBulkUnEnrollModalOpen(true)
									}
									else {
										courseBulkEnrollment ? setBulkEnrollmentConfirmationModalOpen(true) : setBulkEnrollModalOpen(true)
									}
									setSelectedSingular(e.id as number)
								}}
								checked={e.value} disabled={!isCoursePublished}/>
							</span>
						</Tooltip>
					</span>)
			}
		},
		{
			field: "dueDate",
			flex: 1,
			headerName: "Due Date",
			type: "date",
			valueFormatter: (params) => {
				if (!params.value) return "";
				const date = new Date(params.value);
				return date.toLocaleDateString("en-US", {
					timeZone: "UTC"
				});
			}
		},
		{
			field: "enrollmentType",
			flex: 1,
			headerName: "Enrollment Type",
			type: "string",
			valueGetter: (params) =>
				params.value ? t(`user_enrollment_type_${params.value}`, params.value) : ""
		},
		{
			field: "progress",
			flex: 1,
			headerName: "Course Completion",
			type: "number",
			renderCell: e => {
				const {classes} = useStyles();
				return <Stack className={classes.progressBarRoot} direction="row"><LinearProgress className={classes.progressBar} variant="determinate" value={e.value} /><span>{`${e.value}%`}</span></Stack>
			}
		},
		{
			field: "division",
			flex: 1,
			type: "string",
			headerName: "Division"
		},
		{
			field: "supervisorName",
			flex: 1,
			type: "string",
			headerName: "Supervisor"
		}
	])

	const Toolbar= (): JSX.Element => (<CustomToolbar filterButton>
		<Tooltip title={!isCoursePublished ? t("course_draft_not_enrollment_description", "Please publish the course to enable user enrollments.") : ""}>
			<span>
				<Button
					disabled={(selected.length === 0 && !selectedSingular) || isEnrollmentLoading || !isCoursePublished}
					onClick={() => courseBulkEnrollment ? setBulkEnrollmentConfirmationModalOpen(true) : setBulkEnrollModalOpen(true)}
					data-test-id="course-enrollment-bulk-enroll-button"
					aria-label="Enroll selected employees in course"
					sx={{position: "relative"}}
				>
					<Add /> ENROLL
				</Button>
			</span>

		</Tooltip>
		<Tooltip title={!isCoursePublished ? t("course_draft_not_enrollment_description", "Please publish the course to enable user enrollments.") : ""}>
			<span>
				<Button
					disabled={(selected.length === 0 && !selectedSingular) || !isCoursePublished}
					onClick={() => setBulkUnEnrollModalOpen(true)}
					data-test-id="course-enrollment-bulk-unenroll-button"
					aria-label="Unenroll selected employees from course"
				>
					<Undo /> UNENROLL
				</Button>
			</span>
		</Tooltip>
		<Tooltip title={!isCoursePublished ? t("course_draft_not_enrollment_description", "Please publish the course to enable user enrollments.") : ""}>
			<span>
				<Button
					disabled={(selected.length === 0 && !selectedSingular) || !isCoursePublished}
					onClick={() => setBulkCompletionModalOpen(true)}
					data-test-id="course-enrollment-bulk-complete-button"
					aria-label="Mark selected employees as complete for course"
				>
					<Check /> COMPLETE
				</Button>
			</span>
		</Tooltip>

	</CustomToolbar>)

	if(!ownedCohort)
		return <LinearProgress />

	return <Box {...baseProps}>
		<Box className={classes.contentBox}>
			<AdminTable<"user", IUserEntity>
				type="user"
				url={`/api/strapi-plugin-shapeshift-lms/cohort/${ownedCohort?.id}/course/${courseId}/users/enrollments`}
				initialState={{
					sorting: {
						sortModel: [{field: "assignedOn", sort: "desc"}]
					},
					columns: {
						columnVisibilityModel: {
							email: false,
							supervisorName: false,
							hireDate: false,
							titleStartDate: false
						}
					}
				}}
				className={classes.enrollmentDataGrid}
				checkboxSelection
				autoHeight
				aria-label="admin table"
				keepNonExistentRowsSelected
				pagination
				selectionModel={selected}
				onSelectionModelChange={e => setSelected(e as number[])}
				paginationMode="server"
				filterMode="server"
				columns={headCells as any}
				rowsPerPageOptions={[25, 50, 100, 200]}
				components={{Toolbar}}
			>
				{(_, reload, __, ___, clearSelection) => {
					// Store the reload and clearSelection functions in the refs
					refreshDataGridRef.current = reload;
					clearSelectionDataGridRef.current = clearSelection;
					return <>
						<AdminBulkEnrollmentModal courseId={courseId} onClose={(r) =>{ setSelected([]); setSelectedSingular(undefined); setBulkEnrollModalOpen(false); if(r) reload()}} open={bulkEnrollModalOpen} selected={selectedSingular ? [selectedSingular]: selected} />
						<AdminBulkCourseUnenrollModal courseId={courseId} onClose={(r) =>{ setSelected([]); setSelectedSingular(undefined); setBulkUnEnrollModalOpen(false); if(r) reload()}} open={bulkUnEnrollModalOpen} selected={selectedSingular ? [selectedSingular]: selected} />
						<AdminBulkCourseCompletionModal courseId={courseId} onClose={(r) =>{setSelected([]); setSelectedSingular(undefined); setBulkCompletionModalOpen(false); if(r) reload()}} open={bulkCompletionModalOpen} selected={selectedSingular ? [selectedSingular]: selected} />
						{courseBulkEnrollment && (
							<BulkEnrollmentConfirmationModal
								open={bulkEnrollmentConfirmationModalOpen}
								onClose={handleEnrollmentClose}
								selectedUserIds={(selectedSingular ? [selectedSingular] : selected).filter(id => typeof id === "number") as number[]}
								courseId={courseId}
								onLoadingChange={handleEnrollmentLoadingChange}
							/>
						)}
					</>
				}}
			</AdminTable>
		</Box>
	</Box>
}